<script lang="ts" setup>
import { AppButton, AppSelectField, AppTextArea, AppTextField, AppTypography } from '#core/components';
import useSnackbarStore from '#core/store/snackbar';
import type { User } from '#core/types/User';
import { useAuthStore } from '#modules/auth/store/auth';
import { ref } from 'vue';
import ReportSubmittedIcon from '~/assets/img/icon/report-submitted.svg';
import AppConfirmationDialog from '~/components/AppConfirmationDialog.vue';
import { MediaFIleUploader } from '../../media/components';
import type { ReportForm, ReportResponse } from '../types/Report';

const { user: userData } = useAuthStore();
const { showSnackbar } = useSnackbarStore();
const { VALIDATIONS } = useValidation();

const {
  title = 'Report a Problem',
  loading = false,
  reportId = 0,
  reportType = 'posts',
  asListItem = false,
  user = undefined,
  formOnly = false,
  submitButtonText = 'Submit',
  cancelButtonText = 'Cancel',
  cancelButton = true,
} = defineProps<{
  title?: string;
  loading?: boolean;
  reportId: number;
  reportType?: string;
  asListItem?: boolean;
  user?: User;
  formOnly?: boolean;
  submitButtonText?: string;
  cancelButtonText?: string;
  cancelButton?: boolean;
}>();

const emits = defineEmits<{
  (e: 'successfulSubmitAction'): void;
  (e: 'additionalSubmitAction'): void;
}>();

const { data: reportData, pending: isLoading, execute: execSubmit, error: serverError } = useSubmit<ReportResponse>();

const reportTypeOptions = ref();

const files = ref<File[]>([]);
const mediaIds = ref<number[]>([]);
const uploadInProgress = ref(false);

const dialog = ref<boolean>(false);
const reportSuccessful = ref<boolean>(false);

const { form, handleSubmit, getErrors, v$ } = useForm<ReportForm>({
  form: {
    reason_id: null,
    description: null,
    mediaIds: [] as number[],
  },
  rules: {
    reason_id: {
      required: VALIDATIONS.required('Reason'),
    },
    description: {
      required: VALIDATIONS.required('Description'),
    },
  },
});

const uploadMedia = async () => {
  uploadInProgress.value = true;
  mediaIds.value = [];
  for (const file of files.value) {
    const media = await useMediaUploader(file);
    mediaIds.value.push(media);

    if (media?.error) {
      showSnackbar({ text: media.error.message, state: false });
      return;
    }
  }
  uploadInProgress.value = false;
};

async function onSubmit() {
  if (files.value) {
    await uploadMedia();
  }

  handleSubmit(async function () {
    const payload = new FormData();

    payload.append('report_id', reportId);
    payload.append('report_type', reportType);
    payload.append('reason_id', form.value.reason_id?.toString() ?? '');
    payload.append('description', form.value.description);
    mediaIds.value.forEach((mediaId) => payload.append('media_ids[]', mediaId));

    await execSubmit('/report', {
      method: 'POST',
      body: payload,
    });

    if (reportData.value) {
      clearForm();
      dialog.value = false;
      reportSuccessful.value = true;
    }
  });
}

const errors = computed(() => {
  return getErrors(serverError.value?.cause?.errors);
});
const { data, execute: getReportCategories } = useGetData<{ data: {} }>();

onMounted(async () => {
  if (userData?.id) {
    await getReportCategories(`report/categories?filter[type]=${reportType}&limit=100`, {
      method: 'GET',
    });
  }

  reportTypeOptions.value = data.value?.data ?? [];
});

function clearForm() {
  form.value.reason_id = undefined;
  form.value.description = '';
  files.value = [];
  v$.value.$reset();
}

function showDialog() {
  clearForm();
  dialog.value = true;
}
</script>

<template>
  <div>
    <v-dialog v-model="dialog" persistent width="480" data-testid="report-dialog" v-if="!formOnly">
      <template #activator="{ props: dialogProps }">
        <AppButton
          type="button"
          variant="outlined"
          class="mr-4 report-dialog-btn"
          v-bind="dialogProps"
          :disabled="!userData?.id"
          @click="showDialog"
        >
          Report
        </AppButton>
      </template>
      <v-form ref="report-form" data-testid="report-form" @submit.prevent="onSubmit">
        <v-card class="pa-6" flat rounded="xl" height="100%" style="position: relative">
          <v-card-title class="d-flex pa-0 justify-space-between align-center">
            <AppTypography element="h3" type="title-3">{{ title }}</AppTypography>
            <AppButton
              size="small"
              rounded="xl"
              variant="text"
              color="gray"
              icon="mdi mdi-close"
              type="button"
              @click="dialog = false"
            >
            </AppButton>
          </v-card-title>

          <v-card-text class="px-0">
            <AppSelectField
              ref="reason_id"
              v-model="form.reason_id"
              label="Reason"
              :items="reportTypeOptions"
              item-value="id"
              item-title="label"
              :error-messages="errors?.reason_id"
              data-testid="reason_id"
              required
            />

            <AppTextArea
              ref="description"
              v-model="form.description"
              label="Description"
              outlined
              rows="3"
              class="mt-4"
              :error-messages="errors?.description"
              data-testid="description"
              :max-length="280"
              count
              required
            />

            <MediaFIleUploader
              v-model="files"
              :file-types="['image/png', 'image/jpeg', 'image/jpg', 'image/webp']"
              multiple
              :error-messages="errors?.attachments"
              :loading="uploadInProgress"
              data-testid="attachments"
              class="mt-4"
            />

            <v-row class="mt-4">
              <v-col v-if="cancelButton">
                <AppButton
                  block
                  type="button"
                  color="error"
                  variant="tonal"
                  :disabled="uploadInProgress"
                  @click="dialog = false"
                >
                  {{ cancelButtonText }}
                </AppButton>
              </v-col>
              <v-col>
                <AppButton
                  block
                  color="primary"
                  :loading="isLoading ?? loading"
                  :disabled="uploadInProgress"
                  type="submit"
                  data-testid="submit"
                >
                  {{ submitButtonText }}
                </AppButton>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-form>
    </v-dialog>
    <div v-if="formOnly">
      <v-form ref="report-form" data-testid="report-form" @submit.prevent="onSubmit">
        <AppTextField
          v-if="reportType === 'users'"
          :model-value="user?.first_name + ' ' + user?.first_name.charAt(0) + '.'"
          label="User"
          ref="name"
          :items="reportTypeOptions"
          item-value="id"
          item-title="label"
          :error-messages="errors?.reason_id"
          data-testid="name"
          disabled
          single-line
        />

        <AppSelectField
          ref="reason_id"
          v-model="form.reason_id"
          label="Reason"
          :items="reportTypeOptions"
          item-value="id"
          item-title="label"
          :error-messages="errors?.reason_id"
          data-testid="reason_id"
          hide-details="auto"
          required
        />

        <AppTextArea
          ref="description"
          v-model="form.description"
          label="Tell us what happened"
          outlined
          rows="3"
          class="mt-4"
          :error-messages="errors?.description"
          data-testid="description"
          :max-length="280"
          :hide-details="false"
          required
          count
        />

        <MediaFIleUploader
          v-model="files"
          :file-types="['image/png', 'image/jpeg', 'image/jpg', 'image/webp']"
          multiple
          :drag-drop="false"
          :error-messages="errors?.attachments"
          :loading="uploadInProgress"
          data-testid="attachments"
          class="mt-4"
        />

        <v-row class="mt-4">
          <v-col v-if="cancelButton">
            <AppButton
              block
              type="button"
              color="error"
              variant="tonal"
              :disabled="uploadInProgress"
              @click="dialog = false"
            >
              {{ cancelButtonText }}
            </AppButton>
          </v-col>
          <v-col>
            <AppButton
              block
              color="primary"
              :loading="isLoading ?? loading"
              :disabled="uploadInProgress"
              type="submit"
              data-testid="submit"
            >
              {{ submitButtonText }}
            </AppButton>
          </v-col>
        </v-row>
      </v-form>
    </div>
    <AppConfirmationDialog
      v-model="reportSuccessful"
      :icon="ReportSubmittedIcon"
      title="Report Submitted!"
      description="Thanks for letting us know!<br>We will review your report and take action if necessary."
      @button-action="
        emits('successfulSubmitAction');
        reportSuccessful = false;
      "
      @close-action="emits('additionalSubmitAction')"
      :width="391"
    />
  </div>
</template>
